import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import useFileDownload from '../../../hooks/useFiledDownload';
import styles from '../Group.module.scss';
import { useTranslation } from 'react-i18next';

const { Box, Table, DropDown, Title, Button } = vp;

type ContactListType = {
  [key: number]: {
    Account: string;
    Amount: string;
    ClientReferenceNumber: string;
    Currency: string;
    FirstName: string;
    IsActive: string;
    LastName: string;
    Message: string;
    Token: string;
  };
};

type BulkTransactionDetailProps = {
  groupList: ContactListType;
  type: string;
};

type TokenProps = {
  filteredList: ContactListType;
  group: vp.ContactGroupsLists;
};

type FC = FunctionComponent<BulkTransactionDetailProps>;

const BulkTransactionDetails: FC = (props: BulkTransactionDetailProps) => {
  const history = useHistory();
  const { groupList, type } = props;

  const { state } = useLocation<TokenProps>();
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingXLS, setIsExportingXLS] = useState(false);
  const [isExportingDOC, setIsExportingDOC] = useState(false);
  const [isExportingText, setIsExportingText] = useState(false);
  const [isExportingJSON, setIsExportingJSON] = useState(false);
  const [isExportingXML, setIsExportingXML] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Groups.Details.BulkTransaction' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const {
    downloadCsvFile,
    downloadJson,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
    downloadPdfTable,
  } = useFileDownload();

  useEffect(() => {
    if (isExportingPDF) {
      downloadPdfTable('bulk-transaction-overview', 'bulk-transaction');
    }

    if (isExportingCSV) {
      downloadCsvFile('bulk-transaction-overview', 'bulk-transaction');
    }

    if (isExportingXLS) {
      downloadXls('bulkTransactionTable', 'bulk-transaction');
    }

    if (isExportingDOC) {
      downloadDoc('bulkTransactionTable', 'bulk-transaction');
    }

    if (isExportingText) {
      downloadTxt('bulkTransactionTable', 'bulk-transaction');
    }

    if (isExportingJSON) {
      downloadJson(groupList, 'bulk-transaction');
    }

    if (isExportingXML) {
      downloadXml('bulkTransactionTable', 'bulk-transaction');
    }

    if (isPrinting) {
      printFile('bulkTransactionTable');
    }

    setIsExportingPDF(false);
    setIsExportingCSV(false);
    setIsExportingXLS(false);
    setIsExportingDOC(false);
    setIsExportingText(false);
    setIsExportingJSON(false);
    setIsExportingXML(false);
    setIsPrinting(false);
  }, [
    isExportingPDF,
    isExportingCSV,
    isExportingXLS,
    isExportingDOC,
    isExportingText,
    isExportingJSON,
    isExportingXML,
    isPrinting,
  ]);

  const TableProps: vp.TableProps = {
    id: 'bulkTransactionTable',
    name: 'review-bulk-transaction',
    columns: {
      name: { header: <>{type === 'Payment' ? t('Table.payeeName') : t('Table.payerName')}</>, align: 'left' },
      account: { header: t('Table.account'), align: 'left' },
      amount: { header: t('Table.amount'), align: 'left' },
      reference: { header: t('Table.reference'), align: 'left' },
      service: { header: t('Table.Service.label'), align: 'left' },
      message: { header: t('Table.message'), align: 'left' },
    },
    rows:
      Object.values(groupList).map((contact) => ({
        name: <>{`${contact.FirstName} ${contact.LastName}`}</>,
        account: contact.Account,
        amount: <>{`$${contact.Amount} ${state.group.ACTCurrencies_ID === '1' ? 'USD' : 'CAD'} `}</>,
        reference: contact.ClientReferenceNumber,
        service: type === 'Payment' ? t('Table.Service.creditPlaceholder') : t('Table.Service.debitPlaceholder'),
        message: 'Successfully submitted',
      })) || [],
  };

  return (
    <Box flx="col" gap="md">
      <Box jc="sb">
        <Box mb="md" jc="sb">
          <Title title={t('pageTitle')} />
        </Box>
        <Button
          id="back-to-bulk-payment"
          label={t('newBulkTransactionButton')}
          onClick={() => {
            type === 'Payment' ? history.push('/bulk-payment') : history.push('/bulk-collection');
          }}
        />
      </Box>
      <Box width="100%" jc="fe" maxheight="36px">
        <div className={styles.dropdownContainer}>
          <Button
            id="export-transactions"
            color="none"
            label={tGlobal('ExportDropdown.label')}
            height="38px"
            textSize={1}
            background="transparent"
            noShadow
            icon="arrowDown"
            iconLocation="right"
            onClick={() => {
              setIsMenuVisible(true);
            }}
          />
          {isMenuVisible && (
            <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
              <Button
                id="export-PDF-btn"
                label={tGlobal('ExportDropdown.Options.pdf')}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingPDF(true);
                }}
              />
              <Button
                id="export-CSV-btn"
                label={tGlobal('ExportDropdown.Options.csv')}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingCSV(true);
                }}
              />
              <Button
                id="export-XLS-btn"
                label={tGlobal('ExportDropdown.Options.xls')}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingXLS(true);
                }}
              />
              <Button
                id="export-DOC-btn"
                label={tGlobal('ExportDropdown.Options.doc')}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingDOC(true);
                }}
              />
              <Button
                id="export-Text-btn"
                label={tGlobal('ExportDropdown.Options.text')}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingText(true);
                }}
              />
              <Button
                id="export-JSON-btn"
                label={tGlobal('ExportDropdown.Options.json')}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingJSON(true);
                }}
              />
              <Button
                id="export-XML-btn"
                label={tGlobal('ExportDropdown.Options.xml')}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsExportingXML(true);
                }}
              />
              <Button
                id="export-Print-btn"
                label={tGlobal('ExportDropdown.Options.print')}
                background="transparent"
                textSize={1}
                width="100%"
                noShadow
                onClick={() => {
                  setIsPrinting(true);
                }}
              />
            </DropDown>
          )}
        </div>
      </Box>
      <div id="bulk-transaction-overview" data-testid="bulk-transaction-overview">
        <Table {...TableProps} />
      </div>
    </Box>
  );
};

export default BulkTransactionDetails;
